/// <reference types="vite/client" />
import { ServerRegistration } from '../types';
import { sendRegistrationNotification, sendStatusUpdateNotification, sendDeleteNotification } from './discord';

/**
 * URL do Google Apps Script que serve como banco de dados via Google Sheets.
 * Configurada via variável de ambiente VITE_GOOGLE_APPS_SCRIPT_URL.
 */
const DEFAULT_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzUyQpvIr_wvBhkLTqS5EoT9jRIXn8h3Dpx1cQ03juoptdHfIq-18bUZYmtTNhksp1o/exec';
const APPS_SCRIPT_URL = import.meta.env.VITE_GOOGLE_APPS_SCRIPT_URL || DEFAULT_APPS_SCRIPT_URL;

// ============================================
// HELPERS INTERNOS
// ============================================

/**
 * Faz uma requisição GET para o Apps Script com parâmetros na URL.
 * Usado para todas as leituras para evitar perda de dados em redirects CORS do browser.
 */
async function sheetsGet(params?: Record<string, string>): Promise<any> {
  if (!APPS_SCRIPT_URL) throw new Error('Google Apps Script URL não configurada.');

  let url = APPS_SCRIPT_URL;
  if (params) {
    const query = new URLSearchParams(params).toString();
    url += (url.includes('?') ? '&' : '?') + query;
  }

  const response = await fetch(url, {
    method: 'GET',
    redirect: 'follow',
  });

  if (!response.ok) {
    throw new Error(`[SHEETS] GET falhou: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Faz uma requisição POST para o Apps Script com um body JSON.
 * Usado para operações de escrita (add, update, delete, saveSiteData).
 */
async function sheetsPost(body: Record<string, any>): Promise<any> {
  if (!APPS_SCRIPT_URL) throw new Error('Google Apps Script URL não configurada.');

  const response = await fetch(APPS_SCRIPT_URL, {
    method: 'POST',
    redirect: 'follow',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`[SHEETS] POST falhou: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// ============================================
// UPLOAD DE MÍDIA (Imagens & Vídeos)
// Converte arquivos para Base64 Data URL.
// Se for imagem, redimensiona e comprime via HTML5 Canvas (max 1920x1080)
// para economizar espaço e funcionar instantaneamente no painel admin.
// ============================================

export const uploadMedia = async (file: File, _bucket?: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Se for imagem, redimensiona e comprime via Canvas
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 1080;
            let width = img.width;
            let height = img.height;

            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, width, height);
              const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
              const dataUrl = canvas.toDataURL(mimeType, 0.85);
              console.log(`✅ [MEDIA] Imagem otimizada (${width}x${height}).`);
              resolve(dataUrl);
              return;
            }
          } catch (err) {
            console.warn('⚠️ [MEDIA] Falha na compressão, usando Base64 original:', err);
          }
          resolve(e.target?.result as string);
        };
        img.onerror = () => {
          resolve(e.target?.result as string);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    } else {
      // Para vídeos e outros arquivos, converte direto para Data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    }
  });
};

// ============================================
// DATASTORE GLOBAL (conteúdo do site para todos os usuários)
// ============================================

/**
 * Busca o DataStore completo salvo pelo admin no Google Sheets.
 * Usa GET ?action=getSiteData.
 */
export const fetchSiteDataFromSheets = async (): Promise<any | null> => {
  if (!APPS_SCRIPT_URL) return null;
  try {
    const result = await sheetsGet({ action: 'getSiteData' });
    if (result.success && result.data) {
      console.log('☁️ [SHEETS] DataStore carregado do Google Sheets.');
      return result.data;
    }
    return null;
  } catch (e) {
    console.error('❌ [SHEETS] Erro ao buscar DataStore:', e);
    return null;
  }
};

/**
 * Salva o DataStore completo no Google Sheets via PropertiesService.
 */
export const syncSiteDataToSheets = async (data: any): Promise<void> => {
  if (!APPS_SCRIPT_URL) {
    console.warn('⚠️ [SHEETS] URL não configurada, pulando sync do DataStore.');
    return;
  }
  const result = await sheetsPost({ action: 'saveSiteData', data });
  if (!result.success) {
    throw new Error(result.error || 'Falha ao salvar DataStore no Google Sheets.');
  }
  console.log('✅ [SHEETS] DataStore publicado para todos os usuários.');
};

// ============================================
// SENHAS DE ACESSO
// ============================================

/**
 * Busca a senha do administrador via GET ?action=getAdminPassword.
 * O código.gs lê da aba 'admin_access', célula B2.
 */
export const getAdminPassword = async (): Promise<string | null> => {
  if (!APPS_SCRIPT_URL) return null;
  try {
    const result = await sheetsGet({ action: 'getAdminPassword' });
    if (result.success && result.password !== undefined && result.password !== null) {
      return String(result.password).trim();
    }
    console.warn('⚠️ [SHEETS] Senha admin não encontrada na planilha.');
    return null;
  } catch (e) {
    console.error('❌ [SHEETS] Erro ao buscar senha admin:', e);
    return null;
  }
};

/**
 * Busca a senha do funcionário via GET ?action=getEmployeePassword.
 * O código.gs lê da aba 'employee_access', célula B2.
 */
export const getEmployeePassword = async (): Promise<string | null> => {
  if (!APPS_SCRIPT_URL) return null;
  try {
    const result = await sheetsGet({ action: 'getEmployeePassword' });
    if (result.success && result.password !== undefined && result.password !== null) {
      return String(result.password).trim();
    }
    console.warn('⚠️ [SHEETS] Senha funcionário não encontrada na planilha.');
    return null;
  } catch (e) {
    console.error('❌ [SHEETS] Erro ao buscar senha funcionário:', e);
    return null;
  }
};

// ============================================
// CADASTROS DE JOGADORES
// ============================================

/**
 * Busca todos os registros via GET ?action=getRegistrations.
 */
export const fetchRegistrations = async (): Promise<ServerRegistration[]> => {
  if (!APPS_SCRIPT_URL) return [];
  try {
    const result = await sheetsGet({ action: 'getRegistrations' });

    if (!result.success) {
      console.error('❌ [SHEETS] Erro retornado pela API:', result.error);
      return [];
    }

    const rows = result.data || [];
    return rows
      .filter((row: any) => row.id && String(row.id).trim() !== '')
      .map((row: any): ServerRegistration => ({
        id:          String(row.id          || ''),
        user_id:     String(row.user_id     || ''),
        email:       String(row.email       || ''),
        birth_date:  String(row.birth_date  || ''),
        status:      (row.status as 'pending' | 'approved' | 'rejected') || 'pending',
        notes:       String(row.notes       || ''),
        created_at:  String(row.created_at  || ''),
        updated_at:  String(row.updated_at  || ''),
        approved_by: String(row.approved_by || '') || undefined,
        approved_at: String(row.approved_at || '') || undefined,
      }));
  } catch (e) {
    console.error('❌ [SHEETS] Erro ao buscar registros:', e);
    return [];
  }
};

/**
 * Cria um novo cadastro via POST { action: "addRegistration", ...campos }.
 */
export const createRegistration = async (userData: {
  user_id: string;
  email: string;
  birth_date: string;
}): Promise<ServerRegistration | null> => {
  if (!APPS_SCRIPT_URL) {
    throw new Error('Banco de dados não disponível.');
  }

  const existing = await fetchRegistrations();
  const duplicate = existing.find(
    (r) => r.user_id.trim().toLowerCase() === userData.user_id.trim().toLowerCase()
  );
  if (duplicate) {
    throw new Error('Este ID já está cadastrado. Se você já solicitou acesso, aguarde a liberação.');
  }

  const now = new Date().toISOString();
  const newReg: ServerRegistration = {
    id:         `reg_${Date.now()}`,
    user_id:    userData.user_id.trim(),
    email:      userData.email.trim().toLowerCase(),
    birth_date: userData.birth_date,
    status:     'pending',
    created_at: now,
    updated_at: now,
  };

  const result = await sheetsPost({
    action:      'addRegistration',
    id:          newReg.id,
    user_id:     newReg.user_id,
    email:       newReg.email,
    birth_date:  newReg.birth_date,
    status:      newReg.status,
    notes:       '',
    created_at:  newReg.created_at,
    updated_at:  newReg.updated_at,
    approved_by: '',
    approved_at: '',
  });

  if (!result.success) {
    throw new Error(result.error || 'Erro ao processar cadastro. Tente novamente.');
  }

  sendRegistrationNotification({
    user_id:    newReg.user_id,
    email:      newReg.email,
    birth_date: newReg.birth_date,
    created_at: newReg.created_at,
  }).catch((err) => console.warn('⚠️ [DISCORD] Notificação falhou:', err));

  console.log('✅ [SHEETS] Novo cadastro criado:', newReg.user_id);
  return newReg;
};

/**
 * Atualiza o status de um registro via POST { action: "updateStatus", ...campos }.
 */
export const updateRegistrationStatus = async (
  id: string,
  status: 'approved' | 'rejected',
  approvedBy?: string
): Promise<boolean> => {
  if (!APPS_SCRIPT_URL) return false;

  const all = await fetchRegistrations();
  const registration = all.find((r) => r.id === id);
  if (!registration) {
    console.error('❌ [SHEETS] Registro não encontrado para atualizar:', id);
    return false;
  }

  const now = new Date().toISOString();
  const result = await sheetsPost({
    action:      'updateStatus',
    id,
    status,
    approved_by: approvedBy || 'Admin',
    approved_at: now,
    updated_at:  now,
  });

  if (!result.success) {
    console.error('❌ [SHEETS] Erro ao atualizar status:', result.error);
    return false;
  }

  sendStatusUpdateNotification(registration.user_id, status, approvedBy)
    .catch((err) => console.error('⚠️ [SYNC] Erro ao sincronizar status:', err));

  console.log(`✅ [SHEETS] Status atualizado para ${status}:`, id);
  return true;
};

/**
 * Remove um registro via POST { action: "deleteRegistration", id }.
 */
export const deleteRegistration = async (id: string): Promise<boolean> => {
  if (!APPS_SCRIPT_URL) return false;

  const all = await fetchRegistrations();
  const registration = all.find((r) => r.id === id);
  if (!registration) {
    console.error('❌ [SHEETS] Registro não encontrado para deletar:', id);
    return false;
  }

  const result = await sheetsPost({
    action: 'deleteRegistration',
    id,
  });

  if (!result.success) {
    console.error('❌ [SHEETS] Erro ao deletar registro:', result.error);
    return false;
  }

  sendDeleteNotification(registration.user_id)
    .catch((err) => console.error('⚠️ [SYNC] Erro ao remover da planilha:', err));

  console.log('✅ [SHEETS] Registro removido:', id);
  return true;
};

// ============================================
// EXPORTAÇÃO CSV
// ============================================

export const exportRegistrationsToCSV = async (): Promise<string> => {
  const registrations = await fetchRegistrations();

  if (registrations.length === 0) {
    return 'Nenhum registro encontrado.';
  }

  const headers = ['ID', 'User ID', 'Email', 'Data Nascimento', 'Status', 'Data Cadastro', 'Aprovado Por', 'Data Aprovação', 'Notas'];
  const rows = registrations.map((reg) => [
    reg.id,
    reg.user_id,
    reg.email,
    reg.birth_date ? new Date(reg.birth_date).toLocaleDateString('pt-BR') : '',
    reg.status,
    new Date(reg.created_at).toLocaleString('pt-BR'),
    reg.approved_by || '',
    reg.approved_at ? new Date(reg.approved_at).toLocaleString('pt-BR') : '',
    reg.notes || '',
  ]);

  const csvContent = [
    headers.join(';'),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
  ].join('\n');

  return csvContent;
};

export const downloadRegistrationsCSV = async () => {
  const csvContent = await exportRegistrationsToCSV();

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `registros_servidor_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const bulkImportRegistrations = async (
  records: Array<{
    user_id: string;
    email: string;
    birth_date: string;
    status: 'pending' | 'approved' | 'rejected';
    created_at?: string;
    approved_by?: string;
    approved_at?: string;
  }>
): Promise<{ inserted: number; errors: string[] }> => {
  const results = { inserted: 0, errors: [] as string[] };

  for (const record of records) {
    try {
      const now = new Date().toISOString();
      const result = await sheetsPost({
        action:      'addRegistration',
        id:          `reg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        user_id:     record.user_id,
        email:       record.email,
        birth_date:  record.birth_date,
        status:      record.status,
        notes:       '',
        created_at:  record.created_at || now,
        updated_at:  now,
        approved_by: record.approved_by || '',
        approved_at: record.approved_at || '',
      });

      if (result.success) {
        results.inserted++;
      } else {
        results.errors.push(`${record.user_id}: ${result.error || 'Erro desconhecido'}`);
      }
    } catch (err: any) {
      results.errors.push(`${record.user_id}: ${err.message}`);
    }
  }

  console.log(`✅ [IMPORT] ${results.inserted} registros importados, ${results.errors.length} erros.`);
  return results;
};
